// Pure settlement helpers extracted from TripExpenseSummary
// Keeps logic testable without React/rendering concerns.

// Find payer id from expense data and trip members
export const getPayerId = (expense, members = []) => {
  if (expense?.payer_user_id) return expense.payer_user_id;

  // Fallback: try to match by display_name/identifier
  const identifier = expense?.identifier;
  if (!identifier) return null;
  const member = members.find((m) => m.display_name === identifier);
  return member ? member.id : null;
};

// Calculate settlement results
// options: { getPaymentMethodLabel?: (expense) => string }
export const computeBalances = (expenses = [], members = [], splitConfigs = {}, options = {}) => {
  const balances = {};
  const details = {};
  const skipped = [];
  const memberIds = members.map((m) => m.id);
  const nameById = new Map(members.map((m) => [String(m.id), m.display_name]));
  const getName = (id) => nameById.get(String(id)) || 'Unknown';
  const getPaymentMethodLabel = options.getPaymentMethodLabel || (() => 'ไม่ระบุ');

  // init containers
  memberIds.forEach((id) => {
    balances[id] = 0;
    details[id] = [];
  });

  expenses.forEach((exp) => {
    const amount = parseFloat(exp?.amount);
    const payerId = getPayerId(exp, members);
    const config = splitConfigs?.[exp?.id];

    if (Number.isNaN(amount)) {
      skipped.push({ expenseId: exp?.id, reason: 'invalid-amount' });
      return;
    }
    if (!payerId) {
      skipped.push({ expenseId: exp?.id, reason: 'missing-payer' });
      return;
    }
    if (!config) {
      skipped.push({ expenseId: exp?.id, reason: 'missing-config' });
      return;
    }

    // credit payer
    if (balances[payerId] === undefined) {
      balances[payerId] = 0;
      details[payerId] = [];
    }
    balances[payerId] += amount;

    // choose consumers
    let consumers = [];
    if (config.mode === 'equal') {
      consumers = memberIds;
    } else if (config.mode === 'self') {
      consumers = [payerId];
    } else if (config.mode === 'custom') {
      consumers = config.targets || [];
    } else {
      consumers = memberIds; // fallback
    }

    if (!consumers.length) {
      skipped.push({ expenseId: exp?.id, reason: 'no-consumer' });
      return;
    }

    const share = amount / consumers.length;
    const paymentMethod = getPaymentMethodLabel(exp);

    consumers.forEach((cid) => {
      if (balances[cid] === undefined) {
        balances[cid] = 0;
        details[cid] = [];
      }
      balances[cid] -= share;

      if (cid !== payerId) {
        const note = exp?.note || 'ค่าใช้จ่าย';
        const calc = `${amount.toLocaleString()}/${consumers.length}`;
        const payerName = getName(payerId);
        const consumerName = getName(cid);

        details[cid].push({
          note,
          amount: share,
          type: 'debit',
          related: payerName,
          calc,
          paymentMethod,
        });

        details[payerId].push({
          note,
          amount: share,
          type: 'credit',
          related: consumerName,
          calc,
          paymentMethod,
        });
      }
    });
  });

  const settlements = calculateSettlements(balances);
  const imbalance = Object.values(balances).reduce((s, v) => s + v, 0);

  return { balances, details, settlements, skipped, imbalance };
};

// Greedy netting algorithm (unchanged from original UI code)
export const calculateSettlements = (balances = {}) => {
  const debtors = [];
  const creditors = [];

  Object.entries(balances).forEach(([id, amount]) => {
    const val = Math.round(amount * 100) / 100;
    if (val < -0.01) debtors.push({ id, amount: val });
    else if (val > 0.01) creditors.push({ id, amount: val });
  });

  debtors.sort((a, b) => a.amount - b.amount);
  creditors.sort((a, b) => b.amount - a.amount);

  const results = [];
  let i = 0;
  let j = 0;

  while (i < debtors.length && j < creditors.length) {
    let debtor = debtors[i];
    let creditor = creditors[j];

    let amount = Math.min(Math.abs(debtor.amount), creditor.amount);
    amount = Math.round(amount * 100) / 100;

    if (amount > 0) results.push({ from: debtor.id, to: creditor.id, amount });

    debtor.amount += amount;
    creditor.amount -= amount;

    if (Math.abs(debtor.amount) < 0.01) i += 1;
    if (Math.abs(creditor.amount) < 0.01) j += 1;
  }
  return results;
};

